import { z } from "zod";
import {
  bulkRequestSchema,
  bulkSourceSchema,
  bulkMappingSchema,
  bytesHash,
  parseProject,
  parseContext,
  outputSchema,
  type Project,
  type Values,
  type Context,
  type BulkRequest,
  type SourceParameters,
} from "@proof/core";

const identity = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[\w.-]+$/);
const diagnostic = z.object({
  code: z.string(),
  message: z.string(),
  retryable: z.boolean().optional(),
  fieldId: z.string().optional(),
});
const artifact = z
  .object({
    filename: z
      .string()
      .min(1)
      .max(250)
      .refine((value) => !/[\\/\u0000-\u001f]/.test(value)),
    mime: z.enum([
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/tiff",
      "image/svg+xml",
      "application/zip",
    ]),
    bytes: z.number().int().positive().max(200000000),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    downloadAllowed: z.boolean(),
    viewAllowed: z.boolean(),
  })
  .passthrough();
const job = z
  .object({
    id: identity,
    projectId: identity,
    state: z.enum([
      "queued",
      "running",
      "complete",
      "failed",
      "cancelled",
      "quarantined",
    ]),
    error: z.string().optional(),
    artifact: artifact.optional(),
  })
  .passthrough();
export type BulkJobView = z.infer<typeof job>;
const batchView = z
  .object({
    id: identity,
    projectId: identity,
    projectName: z.string(),
    state: z.string(),
    stateVersion: z.number().int().nonnegative(),
    source: bulkSourceSchema,
    mapping: bulkMappingSchema,
    progress: z
      .object({
        total: z.number().int().nonnegative(),
        processed: z.number().int().nonnegative(),
        percent: z.number().min(0).max(100),
      })
      .passthrough(),
    rows: z
      .array(
        z
          .object({
            key: z.string().min(1).max(500),
            sourceRow: z.number().int().positive(),
            state: z.string(),
            diagnostic: diagnostic.optional(),
            job: job.optional(),
          })
          .passthrough(),
      )
      .max(10000),
  })
  .passthrough();
export type BulkBatchView = z.infer<typeof batchView>;
export interface BulkContextInput {
  project: Project;
  versionId?: string;
  partId?: string;
  sourceParameters?: SourceParameters;
  contextProfile?: string;
  forceRefresh?: boolean;
}
export interface BulkGenerationInput extends BulkContextInput {
  profile: Project["outputs"][number];
  contextHash?: string;
  batch: BulkRequest;
}
export interface BulkBatchDefinition {
  project: Project;
  profile: Project["outputs"][number];
  partId?: string;
  context?: Context;
  rows: { sourceRow: number; values: Values }[];
}
export interface BulkPreparedImages {
  rows: {
    sourceRow: number;
    values: Values;
    diagnostics: { fieldId?: string; code: string; message: string }[];
  }[];
}
/** Callbacks are already authorized for one workspace by the embedding host. */
export interface BulkGenerationServices {
  listBatches(signal: AbortSignal): Promise<BulkBatchView[]>;
  readBatch(id: string, signal: AbortSignal): Promise<BulkBatchView>;
  readDefinition(id: string, signal: AbortSignal): Promise<BulkBatchDefinition>;
  createBatch(
    input: BulkGenerationInput,
    signal: AbortSignal,
  ): Promise<BulkBatchView>;
  prepareImages?(
    input: BulkGenerationInput,
    signal: AbortSignal,
  ): Promise<BulkPreparedImages>;
  resolveContext?(
    input: BulkContextInput,
    signal: AbortSignal,
  ): Promise<{ context: Context; hash?: string }>;
  cancelBatch(
    id: string,
    stateVersion: number,
    signal: AbortSignal,
  ): Promise<BulkBatchView>;
  retryRows(
    id: string,
    stateVersion: number,
    rowKeys: string[],
    signal: AbortSignal,
  ): Promise<BulkBatchView>;
  loadArtifact(jobId: string, signal: AbortSignal): Promise<Blob>;
}
export interface BulkDownload {
  blob: Blob;
  filename: string;
  kind: "template" | "record" | "zip";
  batchId?: string;
  jobId?: string;
}
export interface BulkSessionStore {
  read(): unknown;
  write(value: unknown): void;
}
export function bindBulkSessionStore(
  input: BulkSessionStore,
): BulkSessionStore {
  if (
    !input ||
    typeof input.read !== "function" ||
    typeof input.write !== "function"
  )
    throw new TypeError("Provide session read and write callbacks");
  return {
    read: () => structuredClone(input.read.call(input)),
    write: (value) => input.write.call(input, structuredClone(value)),
  };
}
const sheetSchema = z
  .object({
    name: z.string().max(200),
    headers: z.array(z.string().max(32768)).min(1).max(500),
    rows: z.array(z.array(z.string().max(32768)).max(500)).max(10000),
    sourceRows: z.array(z.number().int().positive()).max(10000).optional(),
    headerRow: z.number().int().positive().optional(),
  })
  .passthrough();
export function snapshotBulkSession(raw: unknown) {
  const checked = z
    .object({
      tenantId: z.string(),
      projectId: z.string(),
      versionId: z.string().optional(),
      batchId: identity,
      sheets: z.array(sheetSchema).min(1).max(20),
      sheetIndex: z.number().int().min(0).max(19),
      mapping: z.record(z.string().regex(/^\d+$/), z.string()),
      index: z.number().int().nonnegative(),
      pageIndices: z.record(
        z.string().regex(/^\d+$/),
        z.number().int().nonnegative(),
      ),
    })
    .passthrough()
    .safeParse(raw);
  if (!checked.success) return undefined;
  const value = checked.data,
    sheet = value.sheets[value.sheetIndex];
  if (
    !sheet ||
    value.index >= sheet.rows.length ||
    Object.keys(value.mapping).some(
      (key) => Number(key) >= sheet.headers.length,
    )
  )
    return undefined;
  return value;
}
export function snapshotBulkPreparedImages(
  raw: unknown,
  request: BulkRequest,
): BulkPreparedImages {
  const value = z
    .object({
      rows: z
        .array(
          z.object({
            sourceRow: z.number().int().positive(),
            values: z.record(z.string(), z.unknown()),
            diagnostics: z.array(diagnostic).max(1000),
          }),
        )
        .max(10000),
    })
    .parse(raw);
  const checked = bulkRequestSchema.parse({
    ...request,
    rows: value.rows.map((row) => ({
      sourceRow: row.sourceRow,
      values: row.values,
    })),
  });
  if (
    checked.rows.length !== request.rows.length ||
    checked.rows.some((row, i) => row.sourceRow !== request.rows[i].sourceRow)
  )
    throw new TypeError("Prepared image rows differ from the source");
  return {
    rows: checked.rows.map((row, i) => ({
      ...row,
      diagnostics: value.rows[i].diagnostics,
    })),
  };
}
export function snapshotBulkBatch(
  raw: unknown,
  projectId: string,
): BulkBatchView {
  const value = batchView.parse(raw);
  if (
    value.projectId !== projectId ||
    value.rows.some((row) => row.job && row.job.projectId !== projectId) ||
    new Set(value.rows.map((row) => row.key)).size !== value.rows.length ||
    new Set(value.rows.map((row) => row.sourceRow)).size !== value.rows.length
  )
    throw new TypeError(
      "Batch belongs to another document or has duplicate rows",
    );
  return value;
}
export function snapshotBulkSubmission(
  raw: unknown,
  input: BulkGenerationInput,
): BulkBatchView {
  const batch = snapshotBulkBatch(raw, input.project.id);
  if (
    batch.source.sha256 !== input.batch.source.sha256 ||
    JSON.stringify(batch.mapping) !==
      JSON.stringify(bulkMappingSchema.parse(input.batch.mapping)) ||
    batch.rows.length !== input.batch.rows.length ||
    batch.rows.some(
      (row) =>
        !input.batch.rows.some((source) => source.sourceRow === row.sourceRow),
    )
  )
    throw new TypeError("Queued batch does not match the reviewed submission");
  return batch;
}
/** Detach all structured data and retain prototype callback receivers. */
export function bindBulkGenerationServices(
  input: BulkGenerationServices,
): BulkGenerationServices {
  const required = [
    "listBatches",
    "readBatch",
    "readDefinition",
    "createBatch",
    "cancelBatch",
    "retryRows",
    "loadArtifact",
  ] as const;
  const optional = ["prepareImages", "resolveContext"] as const;
  for (const key of required)
    if (!input || typeof input[key] !== "function")
      throw new TypeError(`Provide a ${key} callback`);
  for (const key of optional)
    if (input[key] !== undefined && typeof input[key] !== "function")
      throw new TypeError(`Provide a ${key} callback`);
  const output: Record<string, unknown> = {};
  for (const key of [...required, ...optional]) {
    const callback = input[key];
    if (!callback) continue;
    output[key] = async (...args: unknown[]) => {
      const signal = args.at(-1) as AbortSignal;
      signal.throwIfAborted();
      const detached = args.slice(0, -1).map((arg) => structuredClone(arg));
      const value = await (callback as Function).apply(input, [
        ...detached,
        signal,
      ]);
      signal.throwIfAborted();
      if (key === "loadArtifact") {
        if (
          Object.prototype.toString.call(value) !== "[object Blob]" ||
          value.size < 1 ||
          value.size > 200000000
        )
          throw new TypeError("Invalid generation artifact");
        const bytes = await Blob.prototype.arrayBuffer.call(value);
        signal.throwIfAborted();
        return new Blob([bytes], { type: value.type });
      }
      return structuredClone(value);
    };
  }
  return output as unknown as BulkGenerationServices;
}
export function snapshotBulkDefinition(
  raw: BulkBatchDefinition,
  project: Project,
  batch: BulkBatchView,
): BulkBatchDefinition {
  const definition = structuredClone(raw),
    parsed = parseProject(definition.project);
  if (
    parsed.id !== project.id ||
    parsed.tenantId !== project.tenantId ||
    (definition.partId &&
      !parsed.parts.some((part) => part.id === definition.partId))
  )
    throw new TypeError("Saved batch belongs to another workspace or document");
  const profile = parsed.outputs.find(
    (profile) => profile.id === definition.profile?.id,
  );
  if (
    !profile ||
    JSON.stringify(outputSchema.parse(profile)) !==
      JSON.stringify(outputSchema.parse(definition.profile))
  )
    throw new TypeError("Saved batch profile is inconsistent");
  const checked = bulkRequestSchema.parse({
    version: 1,
    source: batch.source,
    mapping: batch.mapping,
    rows: definition.rows,
  });
  if (
    checked.rows.length !== batch.rows.length ||
    checked.rows.some(
      (row) => !batch.rows.some((saved) => saved.sourceRow === row.sourceRow),
    )
  )
    throw new TypeError("Saved batch rows are inconsistent");
  return {
    ...definition,
    project: parsed,
    profile,
    rows: checked.rows,
    context: definition.context && parseContext(definition.context),
  };
}
export async function readBulkArtifact(
  job: BulkJobView,
  services: BulkGenerationServices,
  signal: AbortSignal,
): Promise<Blob> {
  job = snapshotBulkBatchJob(job);
  if (job.state !== "complete" || !job.artifact?.downloadAllowed)
    throw new TypeError("This record cannot be downloaded");
  const blob = await bindBulkGenerationServices(services).loadArtifact(
    job.id,
    signal,
  );
  if (
    blob.size !== job.artifact.bytes ||
    blob.type !== job.artifact.mime ||
    bytesHash(new Uint8Array(await blob.arrayBuffer())) !== job.artifact.sha256
  )
    throw new TypeError("Generation artifact does not match its saved record");
  signal.throwIfAborted();
  return blob;
}
function snapshotBulkBatchJob(value: unknown): BulkJobView {
  return job.parse(value);
}
export function downloadBulkArtifact(
  artifact: BulkDownload,
  doc: Document = document,
) {
  const urls = doc.defaultView?.URL || URL,
    url = urls.createObjectURL(artifact.blob),
    anchor = doc.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.click();
  setTimeout(() => urls.revokeObjectURL(url), 1000);
}
